import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import UnpublishedIcon from "@mui/icons-material/Unpublished";
import WarningIcon from "@mui/icons-material/Warning";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  Fab,
  FormControlLabel,
  FormGroup,
  IconButton,
  Radio,
  Stack,
  TextField,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { NO_DISPLAY_IMAGE } from "@/constants/images";
import { PAGE_PADDING } from "@/constants/layout";
import { DRAWER_VIEW } from "@/constants/views";
import { Z_INDEX } from "@/constants/zIndex";
import useImageVariant from "@/hooks/useImageVariant";
import { useAppDispatch, useAppSelector } from "@/store";
import { addItem } from "@/store/slices/cartSlice";
import { closeDrawer } from "@/store/slices/commonSlice";
import {
  removeItemInstructionsFromCache,
  saveItemInstructionsToCache,
} from "@/store/slices/instructionsSlice";
import type { MenuItem, MenuOption, MenuOptionGroup } from "@/types";
import { useRouter } from "next/router";
import { formatCurrency } from "@/utils/format";
import BackFab from "./BackFab";
import BottomDrawer from "./BottomDrawer";
import { modifierBounds } from "./modifierBounds";
import QuantityStepper from "./QuantityStepper";

/**
 * `ITEM_OPTIONS_DRAWER` (`oM` in the live build, `oI` in the dist) — choosing an
 * item's modifiers, its quantity and its note.
 *
 * The layout is a **parallax reveal**, not a scrolling header: the 350px image
 * is `position: sticky; top: 0; z-index: 0`, and the body below it is opaque and
 * one layer higher, so scrolling slides the body up OVER a stationary image.
 * The title bar then sticks at the top of that body, and every modifier group
 * header sticks just under it — which is what `--modifier-title-offset` is for:
 * a CSS custom property set once on the root, so a header at any nesting depth
 * parks at the same line.
 *
 * The title's collapse is driven by an **IntersectionObserver on a zero-height
 * sentinel**, not by a scroll listener — the sentinel is the body's first child,
 * so `threshold: 1` flips the moment the image starts to be covered.
 *
 * ── THREE DEVIATIONS FROM THE ORIGINAL, ALL DELIBERATE ────────────────────
 * These are defects in the shipped original. The settled decision is to FIX
 * them and record them here so a later A/B pass does not "restore" the bug:
 *
 * 1. **A catalogue cache hit closed the drawer.** The original's fetch thunk
 *    short-circuits by THROWING the string `"No need to fetch item"`, and its
 *    failure handler closes the drawer on any non-null throw. This tree reads
 *    the item straight from the menu payload already in the store, so there is
 *    no fetch to short-circuit and the drawer stays open.
 * 2. **Save/Unsave never rendered.** It was passed as `slotProps.endAdornment`,
 *    which MUI ignores — only `slotProps.input.endAdornment` reaches the input.
 *    Here it is on the input, so the star is visible.
 * 3. **The note field was uncontrolled**, so clicking a saved chip updated redux
 *    but left the visible text unchanged. It is controlled here.
 *
 * FAITHFUL, and not to be "improved": the note cap is **50 characters with no
 * counter**, no helper text and no error state; and the CTA always reads
 * `Add to cart`, including when editing an existing line.
 */

/* ------------------------------ geometry ---------------------------------- */

const IMAGE_HEIGHT = 350;
/** Every sticky group header parks at the title bar's height plus 7px. */
const HEADER_OFFSET = "calc(var(--modifier-title-offset) + 7px)";
const NOTE_MAX_LENGTH = 50;

const Root = styled(Stack)({ flex: 1, height: "100%", overflow: "hidden" });

const ScrollContainer = styled(Box)({
  flex: 1,
  height: "100%",
  overflowY: "auto",
});

const ImageFrame = styled(Box)(({ theme }) => ({
  position: "sticky",
  top: 0,
  zIndex: Z_INDEX.NONE,
  width: "100%",
  height: IMAGE_HEIGHT,
  minHeight: IMAGE_HEIGHT,
  // Below `xs` (450) the frame becomes a square, so the image keeps its
  // proportions on a phone instead of being cropped to a letterbox.
  [theme.breakpoints.down("xs")]: {
    height: "100vw",
    minHeight: "100vw",
  },
}));

const ImageClose = styled(Box)({
  position: "absolute",
  zIndex: Z_INDEX.BASE,
  top: PAGE_PADDING,
  left: PAGE_PADDING,
});

const ImageFavorite = styled(Box)({
  position: "absolute",
  zIndex: Z_INDEX.BASE,
  top: PAGE_PADDING,
  right: PAGE_PADDING,
});

const Body = styled(Box)(({ theme }) => ({
  position: "relative",
  zIndex: Z_INDEX.BASE,
  backgroundColor: theme.palette.background.default,
  flex: 1,
}));

const TitleBar = styled(Stack)(({ theme }) => ({
  position: "sticky",
  top: 0,
  zIndex: Z_INDEX.LOW,
  padding: PAGE_PADDING,
  backgroundColor: theme.palette.background.default,
}));

/** The title is clamped to one line; the description to three until tapped. */
const Clamp = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "lines",
})<{ lines: number }>(({ lines }) => ({
  display: "-webkit-box",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: lines > 0 ? lines : "unset",
  overflow: lines > 0 ? "hidden" : "visible",
}));

const GroupHeader = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "minimized",
})<{ minimized: boolean }>(({ theme, minimized }) => ({
  position: "sticky",
  zIndex: Z_INDEX.HIGH,
  top: HEADER_OFFSET,
  // So a programmatic `scrollIntoView({ block: "start" })` lands below the
  // title bar rather than underneath it.
  scrollMarginTop: HEADER_OFFSET,
  padding: `10px ${PAGE_PADDING}px`,
  width: "100%",
  cursor: "pointer",
  transition: "background-color .2s ease-in-out",
  backgroundColor: minimized
    ? theme.palette.background.paper
    : theme.palette.mode === "dark"
      ? "#2B2B2B"
      : "#ffffe0",
}));

const OptionRow = styled(FormControlLabel, {
  shouldForwardProp: (prop) => prop !== "soldOut",
})<{ soldOut?: boolean }>(({ soldOut }) => ({
  height: 60,
  margin: 0,
  padding: `0 ${PAGE_PADDING}px`,
  width: "100%",
  justifyContent: "space-between",
  // `pointer-events` is what blocks a sold-out row; there is no `disabled` on
  // it, so the label still reads normally to assistive tech.
  ...(soldOut
    ? { cursor: "not-allowed", opacity: 0.5, pointerEvents: "none" as const }
    : null),
  "& .MuiFormControlLabel-label": { flex: 1 },
}));

const FooterBar = styled(Stack)(({ theme }) => ({
  padding: PAGE_PADDING,
  boxShadow: theme.shadows[1],
  backgroundColor: theme.palette.background.default,
  zIndex: Z_INDEX.BASE,
}));

/** 500ms nudge when the CTA is pressed with a group still unsatisfied. */
const ShakingButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "shakeKey",
})<{ shakeKey: number }>(({ shakeKey }) => ({
  ...(shakeKey > 0
    ? { animation: "item-cta-shake .5s" }
    : null),
  "@keyframes item-cta-shake": {
    "10%, 30%, 50%, 70%, 90%": { transform: "translateX(-4px)" },
    "20%, 40%, 60%, 80%": { transform: "translateX(4px)" },
  },
}));

/* ------------------------------- modifiers -------------------------------- */

type Selection = Record<string, Record<string, number>>;

/** Return root groups plus only the nested groups activated by selected
 * options. This is the same progressive modifier flow used by POS sizes. */
const visibleModifierGroups = (
  groups: MenuOptionGroup[],
  selection: Selection,
): MenuOptionGroup[] =>
  groups.flatMap((group) => {
    const children = (group.options ?? []).flatMap((option) =>
      (selection[group._id]?.[option._id] ?? 0) > 0
        ? visibleModifierGroups(option.modifiers ?? [], selection)
        : [],
    );
    return [group, ...children];
  });

const seedDefaultSelection = (
  groups: MenuOptionGroup[],
  initial: Selection = {},
): Selection => {
  groups.forEach((group) => {
    const defaults = (group.options ?? []).filter(
      (option) => option.default && !option.sold_out,
    );
    if (defaults.length > 0) {
      initial[group._id] = Object.fromEntries(
        defaults.map((option) => [option._id, Math.max(1, option.quantity ?? 1)]),
      );
    }
    (group.options ?? []).forEach((option) =>
      seedDefaultSelection(option.modifiers ?? [], initial),
    );
  });
  return initial;
};

/**
 * The POS engine treats the aggregate bounds as an additional quantity limit,
 * not as a replacement for the option-count bounds.  The larger configured
 * minimum/maximum wins (with the option count as the maximum fallback). A
 * group is REQUIRED when that effective minimum is above zero.
 */
const GROUP_VARIANT = {
  QUANTITY: "QUANTITY",
  RADIO: "RADIO",
  CHECKBOX: "CHECKBOX",
} as const;

const variantFor = (group: MenuOptionGroup) => {
  if (
    group.min_option_choice_quantity != null ||
    group.max_option_choice_quantity != null
  ) {
    return GROUP_VARIANT.QUANTITY;
  }
  return Number(group.max_num_options) === 1
    ? GROUP_VARIANT.RADIO
    : GROUP_VARIANT.CHECKBOX;
};

const totalChosen = (picked: Record<string, number> | undefined): number =>
  Object.values(picked ?? {}).reduce((sum, quantity) => sum + quantity, 0);

/**
 * Validation, depth-first, first failure wins. The two messages use DIFFERENT
 * separators in their paths in the original — `>` for "too few" and `›` for
 * "too many". That inconsistency is reproduced, not tidied.
 */
const validate = (
  groups: MenuOptionGroup[],
  selection: Selection,
): { errorId: string; message: string } | null => {
  for (const group of groups) {
    const { minEff, maxEff } = modifierBounds(group);
    const chosen = totalChosen(selection[group._id]);
    if (minEff > 0 && chosen < minEff) {
      return {
        errorId: group._id,
        message: `Select ${minEff > 1 ? `${minEff} options` : "an option"} for ${group.name}`,
      };
    }
    if (maxEff > 0 && chosen > maxEff) {
      return {
        errorId: group._id,
        message: `Max ${maxEff > 1 ? `${maxEff} options` : "option"} Selectable for ${group.name}`,
      };
    }
  }
  return null;
};

/* --------------------------------- pieces --------------------------------- */

interface StatusPillProps {
  soldOut: boolean;
  isRequired: boolean;
  fulfilled: boolean;
  anySelected: boolean;
}

/** `iC` — the four-state pill on every group header. */
function StatusPill({
  soldOut,
  isRequired,
  fulfilled,
  anySelected,
}: StatusPillProps) {
  if (soldOut) return <Chip label="Sold Out" size="small" color="error" />;
  if (isRequired) {
    return fulfilled ? (
      <Chip label="Required" size="small" color="success" icon={<CheckIcon />} />
    ) : (
      <Chip label="Required" size="small" color="error" icon={<WarningIcon />} />
    );
  }
  return anySelected ? (
    <Chip label="Optional" size="small" color="info" icon={<CheckIcon />} />
  ) : (
    <Chip label="Optional" size="small" color="info" icon={<AddIcon />} />
  );
}

interface OptionLabelProps {
  option: MenuOption;
  isFree: boolean;
  symbol?: string;
  code?: string;
}

function OptionLabel({ option, isFree, symbol, code }: OptionLabelProps) {
  const price = option.price ?? 0;
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Typography
        variant="body1"
        sx={option.sold_out ? { textDecoration: "line-through" } : undefined}
      >
        {option.name}
      </Typography>
      {option.sold_out && <Chip label="Sold Out" size="small" color="error" />}
      {isFree && <Chip label="Free" size="small" color="success" />}
      {!isFree && price > 0 && (
        <Typography variant="body2" color="text.secondary">
          {"+ "}
          {formatCurrency(price, { symbol, code })}
        </Typography>
      )}
    </Stack>
  );
}

interface GroupProps {
  group: MenuOptionGroup;
  picked: Record<string, number>;
  freeOptionIds: Set<string>;
  symbol?: string;
  code?: string;
  errorId: string | null;
  focusErrorIndex: number;
  totalGroups: number;
  onToggle: (group: MenuOptionGroup, option: MenuOption) => void;
  onQuantity: (
    group: MenuOptionGroup,
    option: MenuOption,
    value: number,
    isAbsolute?: boolean,
  ) => void;
}

function ModifierGroup({
  group,
  picked,
  freeOptionIds,
  symbol,
  code,
  errorId,
  focusErrorIndex,
  totalGroups,
  onToggle,
  onQuantity,
}: GroupProps) {
  const { minEff, maxEff, isRequired } = modifierBounds(group);
  const variant = variantFor(group);
  const options = group.options ?? [];
  const chosen = totalChosen(picked);
  const fulfilled = chosen >= minEff;
  const full = maxEff > 0 && chosen >= maxEff;

  const [minimized, setMinimized] = useState(Boolean(group.minimized));
  const headerRef = useRef<HTMLDivElement>(null);
  const wasFull = useRef(full);
  const scrollTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Expanding scrolls the group under the sticky title bar, debounced so a
  // burst of taps settles on one scroll.
  const scrollHere = useCallback(() => {
    clearTimeout(scrollTimer.current);
    scrollTimer.current = setTimeout(() => {
      headerRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 250);
  }, []);

  useEffect(() => () => clearTimeout(scrollTimer.current), []);

  // Auto-collapse the instant a group becomes full — but never a lone group,
  // where collapsing the only thing on screen would be disorienting.
  useEffect(() => {
    if (full && !wasFull.current && totalGroups > 1) setMinimized(true);
    wasFull.current = full;
  }, [full, totalGroups]);

  // The CTA reports a failing group by bumping a counter; the group answers by
  // collapsing, scrolling itself into view and re-expanding.
  useEffect(() => {
    if (focusErrorIndex === 0 || errorId !== group._id) return;
    setMinimized(true);
    headerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    const timer = setTimeout(() => setMinimized(false), 300);
    return () => clearTimeout(timer);
  }, [focusErrorIndex, errorId, group._id]);

  const toggleMinimized = () => {
    setMinimized((previous) => {
      if (previous) scrollHere();
      return !previous;
    });
  };

  const rows = options.map((option) => {
    const quantity = picked[option._id] ?? 0;
    const selected = quantity > 0;
    const isFree = freeOptionIds.has(option._id);
    const label = (
      <OptionLabel
        option={option}
        isFree={isFree}
        symbol={symbol}
        code={code}
      />
    );

    if (variant === GROUP_VARIANT.QUANTITY) {
      return (
        <OptionRow
          key={option._id}
          soldOut={option.sold_out}
          label={label}
          labelPlacement="start"
          control={
            <QuantityStepper
              quantity={quantity}
              minQuantity={group.min_option_choice_quantity || 1}
              maxQuantity={group.max_option_choice_quantity ?? Infinity}
              minClickable
              showInput
              setQuantity={(value, isAbsolute) =>
                onQuantity(group, option, value, isAbsolute)
              }
            />
          }
        />
      );
    }

    if (variant === GROUP_VARIANT.RADIO) {
      return (
        <OptionRow
          key={option._id}
          soldOut={option.sold_out}
          label={label}
          labelPlacement="start"
          // The radio variant carries no `disabled` at all — its rows stay
          // clickable even when one is already chosen.
          control={
            <Radio
              checked={selected}
              onClick={() => onToggle(group, option)}
              checkedIcon={<CheckCircleIcon />}
            />
          }
        />
      );
    }

    const capped = full && !selected;
    return (
      <OptionRow
        key={option._id}
        soldOut={option.sold_out}
        label={label}
        labelPlacement="start"
        control={
          <Checkbox
            checked={selected}
            disabled={capped}
            onChange={() => onToggle(group, option)}
            icon={
              capped ? <UnpublishedIcon /> : <RadioButtonUncheckedIcon />
            }
            checkedIcon={<CheckCircleIcon />}
          />
        }
      />
    );
  });

  return (
    <Box
      sx={
        group.sold_out
          ? { cursor: "not-allowed", opacity: 0.5, pointerEvents: "none" }
          : undefined
      }
    >
      <GroupHeader
        ref={headerRef}
        minimized={minimized}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        onClick={toggleMinimized}
      >
        <Stack>
          <Typography
            variant="subtitle1"
            color={group.sold_out ? "text.disabled" : "text.primary"}
          >
            {group.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {`(${chosen}/${maxEff} selected)`}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          <StatusPill
            soldOut={Boolean(group.sold_out)}
            isRequired={isRequired}
            fulfilled={fulfilled}
            anySelected={chosen > 0}
          />
          {minimized ? <ExpandMoreIcon /> : <ExpandLessIcon />}
        </Stack>
      </GroupHeader>

      <Collapse in={!minimized} unmountOnExit>
        <FormGroup>{rows}</FormGroup>
      </Collapse>
    </Box>
  );
}

/* ---------------------------- special requests ---------------------------- */

interface SpecialRequestsProps {
  value: string;
  onChange: (value: string) => void;
}

function SpecialRequests({ value, onChange }: SpecialRequestsProps) {
  const dispatch = useAppDispatch();
  const isStaff = useAppSelector((state) => state.auth.isStaff);
  const saved = useAppSelector((state) => state.instructions.saved);
  const headingRef = useRef<HTMLDivElement>(null);
  const [draft, setDraft] = useState(value);

  useEffect(() => setDraft(value), [value]);

  const isSaved = saved.includes(draft.trim());

  // Lifts the section above the on-screen keyboard once it has opened.
  const scrollIntoView = () => {
    setTimeout(() => {
      headingRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 500);
  };

  return (
    <Stack spacing={1} sx={{ p: `${PAGE_PADDING}px` }}>
      <Typography ref={headingRef} variant="h6">
        Special Requests
      </Typography>
      <Typography variant="caption" color="text.secondary">
        We’ll try our best to accommodate requests, but can’t make changes that
        affect pricing.
      </Typography>

      <TextField
        placeholder="Add a short note to your item"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        // The value is committed on blur, trimmed — typing does not touch the
        // order until the field is left.
        onBlur={() => onChange(draft.trim())}
        onClick={scrollIntoView}
        onFocus={scrollIntoView}
        fullWidth
        multiline
        // A bare native cap: no counter, no helper text, no error state.
        inputProps={{ maxLength: NOTE_MAX_LENGTH }}
        InputProps={
          isStaff
            ? {
                endAdornment: (
                  <Tooltip title={isSaved ? "Unsave" : "Save"}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={draft.trim().length === 0}
                        onClick={() =>
                          dispatch(
                            isSaved
                              ? removeItemInstructionsFromCache(draft.trim())
                              : saveItemInstructionsToCache(draft.trim()),
                          )
                        }
                      >
                        {isSaved ? <StarIcon /> : <StarBorderIcon />}
                      </IconButton>
                    </span>
                  </Tooltip>
                ),
              }
            : undefined
        }
      />

      {saved.length > 0 && (
        <Stack spacing={1}>
          <Typography variant="subtitle2">Saved Item instructions</Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
            {saved.map((entry) => (
              <Chip
                key={entry}
                label={entry}
                size="small"
                onClick={() => {
                  setDraft(entry);
                  onChange(entry);
                }}
                onDelete={() =>
                  dispatch(removeItemInstructionsFromCache(entry))
                }
              />
            ))}
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}

/* ---------------------------------- drawer -------------------------------- */

function ItemOptionsPanel({ item }: { item: MenuItem }) {
  const dispatch = useAppDispatch();
  const { currency_symbol: symbol, currency_code: code } = useAppSelector(
    (state) => state.settings.data,
  );

  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const titleBarRef = useRef<HTMLDivElement>(null);

  const [collapsed, setCollapsed] = useState(false);
  const [modifierTitleOffset, setModifierTitleOffset] = useState(0);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [instructions, setInstructions] = useState("");
  const [focusErrorIndex, setFocusErrorIndex] = useState(0);

  const rootGroups = useMemo(() => item.option_groups ?? [], [item]);

  const [selection, setSelection] = useState<Selection>(() =>
    seedDefaultSelection(rootGroups),
  );
  const groups = useMemo(
    () => visibleModifierGroups(rootGroups, selection),
    [rootGroups, selection],
  );

  const favoriteKey = `zenvix:item-favorite:${item._id}`;
  const [favorite, setFavorite] = useState(false);
  useEffect(() => {
    try {
      setFavorite(window.localStorage.getItem(favoriteKey) === "1");
    } catch {
      setFavorite(false);
    }
  }, [favoriteKey]);
  const toggleFavorite = () => {
    setFavorite((current) => {
      const next = !current;
      try {
        window.localStorage.setItem(favoriteKey, next ? "1" : "0");
      } catch {
        // The visual state still works when private storage is unavailable.
      }
      return next;
    });
  };

  const { imageSrc, onImageError } = useImageVariant(
    item.image_url || undefined,
    "512x512",
    NO_DISPLAY_IMAGE,
  );

  // One observer, created once. The deps really are empty in the original,
  // which is why the recorded offset is the title bar's INITIAL height — with
  // the description clamped — and never updates when it is expanded.
  useEffect(() => {
    const root = scrollRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel || !titleBarRef.current) return;

    setModifierTitleOffset(titleBarRef.current.offsetHeight ?? 0);

    const observer = new IntersectionObserver(
      (entries) => {
        // Only the first entry is read.
        setCollapsed(!entries[0]?.isIntersecting);
      },
      { root, rootMargin: "0px", threshold: 1 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  /**
   * The `num_free_options` cheapest chosen options in a group are free. Ties
   * are broken by the order the API sent, so the result is stable.
   */
  const freeOptionIds = useMemo(() => {
    const free = new Set<string>();
    groups.forEach((group) => {
      const count = group.num_free_options ?? 0;
      if (count <= 0) return;
      const chosen = (group.options ?? []).filter(
        (option) => (selection[group._id]?.[option._id] ?? 0) > 0,
      );
      chosen
        .slice()
        .sort(
          (a, b) =>
            (a.price ?? 0) * (selection[group._id]?.[a._id] ?? 1) -
            (b.price ?? 0) * (selection[group._id]?.[b._id] ?? 1),
        )
        .slice(0, count)
        .forEach((option) => free.add(option._id));
    });
    return free;
  }, [groups, selection]);

  const chosenOptions = useMemo(
    () =>
      groups.flatMap((group) =>
        (group.options ?? [])
          .filter((option) => (selection[group._id]?.[option._id] ?? 0) > 0)
          .map((option) => ({
            option,
            quantity: selection[group._id]?.[option._id] ?? 1,
          })),
      ),
    [groups, selection],
  );

  const modifiersPrice = chosenOptions.reduce(
    (sum, { option, quantity: optionQuantity }) =>
      sum +
      (freeOptionIds.has(option._id) ? 0 : (option.price ?? 0)) *
        optionQuantity,
    0,
  );

  // `discounted_price` is what the customer actually pays; `price` is what gets
  // struck through when the two differ. Both of these are DISPLAY figures for
  // this drawer's own footer — the basket line stores the two BASE prices below
  // and re-derives the same sum through `unitPrice()`.
  const baseUnit = item.price ?? 0;
  const chargedBaseUnit = item.discounted_price ?? item.price ?? 0;
  const listUnit = baseUnit + modifiersPrice;
  const chargedUnit = chargedBaseUnit + modifiersPrice;
  const grossTotal = listUnit * quantity;
  const subtotal = chargedUnit * quantity;
  const discounted = chargedUnit < listUnit;

  const modifierError = useMemo(
    () => validate(groups, selection),
    [groups, selection],
  );
  // A zero base price is valid while a required size is still unselected, but
  // it is never a valid completed catalog configuration unless the item's
  // undiscounted unit (base + selected modifiers) resolves above zero. Use the
  // LIST unit here so a legitimate 100%-discounted item remains purchasable.
  const priceError =
    Number.isFinite(listUnit) && listUnit > 0
      ? null
      : {
          errorId: "__price__",
          message:
            "This item's price is not configured. Please choose a priced option or contact the store.",
        };
  const error = modifierError ?? priceError;

  const toggle = (group: MenuOptionGroup, option: MenuOption) => {
    setSelection((previous) => {
      const picked = { ...(previous[group._id] ?? {}) };
      const selected = (picked[option._id] ?? 0) > 0;

      if (variantFor(group) === GROUP_VARIANT.RADIO) {
        // With a minimum of one there is nothing to fall back to, so a chosen
        // option cannot be un-chosen — only replaced.
        if (selected && Number(group.min_num_options) === 1) return previous;
        return {
          ...previous,
          [group._id]: selected ? {} : { [option._id]: 1 },
        };
      }

      if (selected) delete picked[option._id];
      else {
        const checkboxMax = Number(
          group.max_num_options ?? group.options?.length ?? 0,
        );
        if (checkboxMax > 0 && Object.keys(picked).length >= checkboxMax) {
          return previous;
        }
        picked[option._id] = 1;
      }
      return { ...previous, [group._id]: picked };
    });
  };

  const setOptionQuantity = (
    group: MenuOptionGroup,
    option: MenuOption,
    value: number,
    isAbsolute?: boolean,
  ) => {
    setSelection((previous) => {
      const picked = { ...(previous[group._id] ?? {}) };
      const current = picked[option._id] ?? 0;
      const requested = isAbsolute ? value : current + value;
      const min = Number(group.min_option_choice_quantity) || 1;
      const perOptionMax = Number(group.max_option_choice_quantity ?? Infinity);
      const { maxEff } = modifierBounds(group);
      const quantityMax = Number(group.max_aggregate_options_quantity) || maxEff;
      const otherQuantity = totalChosen(picked) - current;
      const aggregateRoom =
        quantityMax > 0
          ? Math.max(0, quantityMax - otherQuantity)
          : Infinity;
      const next = Math.min(requested, perOptionMax, aggregateRoom);
      // Below the minimum means DESELECT — that is how the minus button at the
      // floor removes an option rather than sticking at 1.
      if (next < min) delete picked[option._id];
      else picked[option._id] = next;
      return { ...previous, [group._id]: picked };
    });
  };

  const submit = () => {
    if (error) {
      setFocusErrorIndex((index) => index + 1);
      return;
    }
    dispatch(
      addItem({
        itemId: item._id,
        name: item.name,
        // The BARE unit price. The original's cart line carries the item's own
        // price and nothing else (`convertOrderEngineItemToOrderItem`), and its
        // line total is `(item.price + modifiersPrice) * quantity`. Storing
        // `chargedUnit` here — the base WITH the options already folded in —
        // while also handing the options over below is what billed the
        // surcharge twice.
        price: chargedBaseUnit,
        // The undiscounted unit, so the basket can total up what was saved.
        // Bare for the same reason: the saving is `listPrice - price`, and the
        // options cancel out of that subtraction anyway.
        listPrice: baseUnit,
        quantity,
        note: instructions,
        // `g()`'s literal: the option's own price, the quantity chosen, and
        // whether the group's free allowance covered it. `modifiersPrice()`
        // applies all three.
        modifiers: chosenOptions.map(({ option, quantity: optionQuantity }) => ({
          _id: option._id,
          ref: option.ref,
          name: option.name,
          price: option.price ?? 0,
          quantity: optionQuantity,
          is_free: freeOptionIds.has(option._id),
        })),
      }),
    );
    dispatch(closeDrawer());
  };

  const close = () => dispatch(closeDrawer());

  return (
    <Root
      sx={{
        "--modifier-title-offset": `${modifierTitleOffset}px`,
      }}
    >
      <ScrollContainer ref={scrollRef}>
        <ImageFrame>
          <img
            src={imageSrc}
            alt={item.name || "Catalog"}
            loading="lazy"
            onError={onImageError}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center 50%",
            }}
          />
          <ImageClose>
            {/*
              Red here only, via the call site rather than `BackFab` itself —
              this is the ✕ the operator asked to stand out, and BackFab is
              shared by every drawer in the app, staff and customer alike.
            */}
            <BackFab onClick={close} title="Close" color="error">
              <CloseIcon fontSize="medium" />
            </BackFab>
          </ImageClose>
          <ImageFavorite>
            <Tooltip title={favorite ? "Remove from favourites" : "Add to favourites"}>
              <Fab
                size="medium"
                color={favorite ? "error" : "default"}
                onClick={toggleFavorite}
                aria-label={favorite ? "Remove from favourites" : "Add to favourites"}
              >
                {favorite ? <FavoriteIcon /> : <FavoriteBorderIcon />}
              </Fab>
            </Tooltip>
          </ImageFavorite>
        </ImageFrame>

        <Body>
          {/* Zero-height sentinel: the observer watches THIS, not the scroll
              position, so the title collapses exactly when the body starts to
              cover the image. */}
          <div ref={sentinelRef} />

          <TitleBar
            ref={titleBarRef}
            spacing={1}
            sx={{ boxShadow: collapsed ? 1 : 0 }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Collapse in={collapsed} unmountOnExit orientation="horizontal">
                <Fab title="close" size="medium" color="secondary" onClick={close}>
                  <CloseIcon />
                </Fab>
              </Collapse>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1.5}
                sx={{ flex: 1, minWidth: 0 }}
              >
                <Clamp variant="h6" lines={1}>
                  {item.name}
                </Clamp>
                <Chip
                  label="Menu"
                  color="primary"
                  icon={<MenuBookIcon />}
                  onClick={close}
                />
              </Stack>
            </Stack>
            {item.description && (
              <Clamp
                variant="body2"
                color="text.secondary"
                lines={descriptionExpanded ? 0 : 3}
                onClick={() => setDescriptionExpanded((open) => !open)}
              >
                {item.description}
              </Clamp>
            )}
          </TitleBar>

          {groups.length > 0 && <Divider />}

          {groups.map((group) => (
            <ModifierGroup
              key={group._id}
              group={group}
              picked={selection[group._id] ?? {}}
              freeOptionIds={freeOptionIds}
              symbol={symbol}
              code={code}
              errorId={error?.errorId ?? null}
              focusErrorIndex={focusErrorIndex}
              totalGroups={groups.length}
              onToggle={toggle}
              onQuantity={setOptionQuantity}
            />
          ))}

          <SpecialRequests value={instructions} onChange={setInstructions} />
        </Body>
      </ScrollContainer>

      <FooterBar direction="row" alignItems="center" spacing={2}>
        <QuantityStepper
          quantity={quantity}
          minQuantity={1}
          setQuantity={(value, isAbsolute) =>
            setQuantity((current) =>
              Math.max(1, isAbsolute ? value : current + value),
            )
          }
        />
        {/* Never disabled by a validation error — pressing it is how the
            customer is TOLD which group is unsatisfied. */}
        <ShakingButton
          key={focusErrorIndex}
          shakeKey={focusErrorIndex}
          fullWidth
          variant="contained"
          size="large"
          disableElevation
          color={error && focusErrorIndex > 0 ? "error" : "primary"}
          disabled={Boolean(item.sold_out)}
          startIcon={
            error && focusErrorIndex > 0 ? <ErrorOutlineIcon /> : undefined
          }
          onClick={submit}
        >
          Add to cart
          {discounted && (
            <Typography
              component="span"
              sx={{ ml: 1, textDecoration: "line-through", opacity: 0.7 }}
            >
              {" "}
              {formatCurrency(grossTotal, { symbol, code })}
            </Typography>
          )}
          <Typography component="span" sx={{ ml: 1, fontWeight: 700 }}>
            {" "}
            {formatCurrency(subtotal, { symbol, code })}
          </Typography>
        </ShakingButton>
      </FooterBar>

      {error && focusErrorIndex > 0 && (
        <Typography
          variant="caption"
          color="error"
          sx={{ px: `${PAGE_PADDING}px`, pb: 1 }}
        >
          {error.message}
        </Typography>
      )}
    </Root>
  );
}

export default function ItemOptionsDrawer() {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const drawer = useAppSelector((state) => state.common.drawer);
  const item = useAppSelector(
    (state) => state.common.drawerPayload,
  ) as MenuItem | null;

  const open = drawer === DRAWER_VIEW.ITEM_OPTIONS && Boolean(item);
  const [contentItem, setContentItem] = useState<MenuItem | null>(null);

  // Commit the lightweight drawer shell in the input task, then build the
  // image/modifier form on the following frame. This gives the click an
  // immediate visual response and prevents the full options tree from sharing
  // one long synchronous React commit with the pointer event.
  useEffect(() => {
    if (!open || !item) {
      setContentItem(null);
      return undefined;
    }
    const frame = requestAnimationFrame(() => setContentItem(item));
    return () => cancelAnimationFrame(frame);
  }, [item, open]);

  useEffect(() => {
    if (open || router.pathname !== "/menu/item/[slug]") return;
    const { slug: _slug, ...query } = router.query;
    void router.replace({ pathname: "/menu", query }, undefined, {
      shallow: true,
      scroll: false,
    });
  }, [open, router]);

  return (
    <BottomDrawer
      open={open}
      onClose={() => dispatch(closeDrawer())}
      title={item?.name ?? ""}
      hideHeader
      disableBodyPadding
      transitionDuration={120}
    >
      {/* Keyed on the item so every open starts from a clean selection rather
          than inheriting the previous item's. */}
      {open && item && contentItem?._id !== item._id && (
        <Stack
          role="status"
          aria-live="polite"
          alignItems="center"
          justifyContent="center"
          sx={{ minHeight: 160, px: 2 }}
        >
          <Typography variant="subtitle1" fontWeight={700}>
            {item.name}
          </Typography>
        </Stack>
      )}
      {open && contentItem && (
        <ItemOptionsPanel key={contentItem._id} item={contentItem} />
      )}
    </BottomDrawer>
  );
}
